import { PortalPageContent, PortalPageContentHeader } from "@fero/layouts";
import type { PurchaseRequestsDashboardSection } from "@fero/pages";
import { useTradeSalesStore } from "@fero/stores";
import { AppRoutes, getPurchaseRequestStatusChipConfig } from "@fero/utils";
import { OpenInNew } from "@mui/icons-material";
import { Alert, Button, Stack } from "@mui/material";
import { Box } from "@mui/system";
import type { GridComparatorFn, GridSortModel } from "@mui/x-data-grid";
import type { WithDbId } from "adl-gen/common/db";
import type {
  NominatedPurchaserDetails,
  PurchaseRequestView,
} from "adl-gen/ferovinum/app/api";
import type {
  Currency,
  NominatedPurchaserTerms,
  Organisation,
  PurchaseRequestState,
} from "adl-gen/ferovinum/app/db";
import { TabbedPage } from "components/library/widgets/tabbed-page";
import { Chip } from "components/widgets";
import { PurchaseRequestProgressStepper } from "components/widgets/base-progress-stepper/purchase-request-progress-stepper";
import { GenerateCSVAndEmailWithRange } from "components/widgets/csv/csv-download-with-range";
import {
  GridTable,
  GridTableColumnFactory,
} from "components/widgets/grid-table/grid-table";
import { LoadingBackdrop } from "components/widgets/loader/loading-backdrop";
import type React from "react";
import { useState } from "react";
import { createBigDecimalCurrencyFormatter } from "utils/currency-utils";
import { formatLocalDate } from "utils/date-utils";

export interface CreditModalValues {
  organisations: WithDbId<Organisation>[];
  nominatedPurchaserDetails: NominatedPurchaserDetails[];
  nominatedPurchaserTerms: WithDbId<NominatedPurchaserTerms>[];
}

export interface PurchaseRequestsPageViewProps {
  inProgressPurchaseRequests: PurchaseRequestView[];
  completedPurchaseRequests: PurchaseRequestView[];
  onSelectSection: (section: PurchaseRequestsDashboardSection) => void;
  completedPurchaseRequestsLoaded: boolean;
  onGenerateCSV: (from: Date, to: Date) => Promise<void>;
}

const nextStateDeadlineComparator: GridComparatorFn = (v1, v2) => {
  const date1 = new Date(v1 as string);
  const date2 = new Date(v2 as string);
  if (Number.isNaN(date1.getTime())) return 1;
  if (Number.isNaN(date2.getTime())) return -1;
  return date1.getTime() - date2.getTime();
};

interface InProgressTableProps {
  inProgressPurchaseRequests: PurchaseRequestView[];
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  handleGenerateCSV: (from: Date, to: Date) => Promise<void>;
}

interface InProgressRowData {
  id?: string | number;
  purchaseRequestRef: string;
  purchaseRequestId: string;
  purchaser: string;
  purchaserInvoiceAmount: number;
  purchaserInvoiceDueDate: string;
  currency: Currency;
  progress: React.JSX.Element;
  nextState: PurchaseRequestState | null;
  nextStateDeadline: string | null;
}

const InProgressTable = ({
  inProgressPurchaseRequests,
  sortModel,
  onSortModelChange,
  handleGenerateCSV,
}: InProgressTableProps) => {
  const inProgressRowData: InProgressRowData[] = inProgressPurchaseRequests.map(
    (prView) => {
      const pr = prView.purchaseRequest.value;
      return {
        purchaseRequestRef: pr.purchaseRequestNumber,
        purchaseRequestId: prView.purchaseRequest.id,
        purchaser: prView.purchaser.value.name,
        purchaserInvoiceAmount: Number.parseFloat(
          prView.purchaserInvoiceAmount,
        ),
        purchaserInvoiceDueDate: formatLocalDate(prView.invoiceDueDate),
        currency: pr.purchaserCurrency,
        progress: (
          <PurchaseRequestProgressStepper
            upcomingStateDeadline={prView.nextStateDeadline ?? undefined}
            currentState={pr.stateEvents[pr.stateEvents.length - 1].state}
            lastStateSet={pr.stateEvents[pr.stateEvents.length - 1].time}
          />
        ),
        nextState: prView.nextState,
        nextStateDeadline: prView.nextStateDeadline,
      };
    },
  );

  const columnFactory = new GridTableColumnFactory<InProgressRowData>();
  const columns = [
    columnFactory.makeHyperLinkColumn({
      header: "Reference No.",
      name: "purchaseRequestRef",
      hrefGetter: (row) =>
        `${AppRoutes.OrganisationPurchaseRequestDetails}/${row.purchaseRequestId}`,
      getter: (row) => row.purchaseRequestRef,
      icon: OpenInNew,
      wrap: true,
    }),
    columnFactory.makeTextColumn({
      header: "Purchaser",
      name: "purchaser",
      getter: (row) => row.purchaser,
    }),
    columnFactory.makeProgressStepperColumn({
      header: "Progress",
      key: "progress",
      align: "center",
      flex: 3,
      sortComparator: nextStateDeadlineComparator,
    }),
    columnFactory.makeDateColumn({
      header: "Invoice Due Date",
      key: "purchaserInvoiceDueDate",
      flex: 0.7,
      displayFormatter: (row) => (
        <Stack alignItems="center">
          {row.nextState &&
            row.nextStateDeadline &&
            formatLocalDate(row.purchaserInvoiceDueDate)}
        </Stack>
      ),
    }),
    columnFactory.makeNumberColumn({
      header: "Invoice Amount",
      key: "purchaserInvoiceAmount",
      flex: 0.7,
      displayFormatter: (row) => (
        <Stack alignItems="center">
          {createBigDecimalCurrencyFormatter(row.currency)().format(
            row.purchaserInvoiceAmount.toString(),
          )}
        </Stack>
      ),
    }),
  ];

  return (
    <GridTable
      columns={columns}
      inputRowsField={inProgressRowData}
      tableHeight="auto"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      searchField
      rowHeight={150}
      csvDownload
      CustomDownload={
        <GenerateCSVAndEmailWithRange generateCSV={handleGenerateCSV} />
      }
    />
  );
};

interface CompletedTableProps {
  completedPurchaseRequests: PurchaseRequestView[];
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  handleGenerateCSV: (from: Date, to: Date) => Promise<void>;
}

interface CompletedRowData {
  id?: string | number;
  purchaseRequestRef: string;
  purchaseRequestId: string;
  purchaser: string;
  purchaserInvoiceAmount: number;
  time: string;
  currency: Currency;
  state: PurchaseRequestState;
}

const CompletedTable = ({
  completedPurchaseRequests,
  sortModel,
  onSortModelChange,
  handleGenerateCSV,
}: CompletedTableProps) => {
  const completedRowsData: CompletedRowData[] = completedPurchaseRequests.map(
    (prView) => ({
      purchaseRequestRef: prView.purchaseRequest.value.purchaseRequestNumber,
      purchaseRequestId: prView.purchaseRequest.id,
      purchaser: prView.purchaser.value.name,
      purchaserInvoiceAmount: Number.parseFloat(prView.purchaserInvoiceAmount),
      time: formatLocalDate(
        new Date(
          prView.purchaseRequest.value.stateEvents[
            prView.purchaseRequest.value.stateEvents.length - 1
          ].time,
        )
          .toISOString()
          .split("T")[0],
      ),
      currency: prView.purchaseRequest.value.purchaserCurrency,
      state: prView.purchaseRequest.value.state,
    }),
  );

  const columnFactory = new GridTableColumnFactory<CompletedRowData>();
  const columns = [
    columnFactory.makeHyperLinkColumn({
      header: "Reference No.",
      name: "purchaseRequestRef",
      hrefGetter: (row) =>
        `${AppRoutes.OrganisationPurchaseRequestDetails}/${row.purchaseRequestId}`,
      getter: (row) => row.purchaseRequestRef,
      icon: OpenInNew,
      wrap: true,
    }),
    columnFactory.makeTextColumn({
      header: "Purchaser",
      name: "purchaser",
      getter: (row) => row.purchaser,
    }),
    columnFactory.makeNodeColumn({
      header: "Progress",
      key: "progress",
      renderNode: (row) => {
        const statusConfig = getPurchaseRequestStatusChipConfig(row.state);
        return (
          <Chip
            label={statusConfig.displayText}
            variant={statusConfig.variant}
          />
        );
      },
    }),
    columnFactory.makeTextColumn({
      header: "Invoice Amount",
      name: "purchaserInvoiceAmount",
      getter: (row) =>
        createBigDecimalCurrencyFormatter(row.currency)().format(
          row.purchaserInvoiceAmount.toString(),
        ),
    }),
    columnFactory.makeDateColumn({
      header: "Completion Date",
      key: "time",
      displayFormatter: (row) => formatLocalDate(row.time),
    }),
  ];

  return (
    <GridTable
      columns={columns}
      inputRowsField={completedRowsData}
      tableHeight="auto"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      searchField
      csvDownload
      CustomDownload={
        <GenerateCSVAndEmailWithRange generateCSV={handleGenerateCSV} />
      }
    />
  );
};

export const PurchaseRequestsPageView = ({
  inProgressPurchaseRequests,
  completedPurchaseRequests,
  onSelectSection,
  completedPurchaseRequestsLoaded,
  onGenerateCSV,
}: PurchaseRequestsPageViewProps) => {
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "time", sort: "desc" },
  ]);
  const { updateOptions, clearEditState } = useTradeSalesStore();

  const hasInProgressPurchaseRequests = inProgressPurchaseRequests.length > 0;
  const hasCompletedPurchaseRequests =
    completedPurchaseRequests.length > 0 && completedPurchaseRequestsLoaded;

  return (
    <PortalPageContent
      header={
        <PortalPageContentHeader
          title="Trade Sales"
          variant="split"
          right={
            <Button
              onClick={() => {
                updateOptions({ isEditMode: false });
                clearEditState();
              }}
              href={AppRoutes.OrganisationPurchaseRequestSetup}
            >
              Create Order
            </Button>
          }
        />
      }
    >
      <TabbedPage
        defaultTab="inProgress"
        handleTabChange={({ key }) =>
          onSelectSection(key as PurchaseRequestsDashboardSection)
        }
        tabs={[
          {
            label: "In Progress",
            key: "inProgress",
            content: (
              <Box>
                {hasInProgressPurchaseRequests ? (
                  <InProgressTable
                    inProgressPurchaseRequests={inProgressPurchaseRequests}
                    sortModel={sortModel}
                    onSortModelChange={(newModel: GridSortModel) =>
                      setSortModel(newModel)
                    }
                    handleGenerateCSV={onGenerateCSV}
                  />
                ) : (
                  <Alert severity="info">
                    There are currently no purchase requests in progress
                  </Alert>
                )}
              </Box>
            ),
          },
          {
            label: "Completed",
            key: "completed",
            content: (
              <Box>
                {hasCompletedPurchaseRequests ? (
                  <CompletedTable
                    completedPurchaseRequests={completedPurchaseRequests}
                    sortModel={sortModel}
                    onSortModelChange={(newModel: GridSortModel) =>
                      setSortModel(newModel)
                    }
                    handleGenerateCSV={onGenerateCSV}
                  />
                ) : completedPurchaseRequestsLoaded ? (
                  <Alert severity="info">
                    There are currently no completed purchase requests
                  </Alert>
                ) : (
                  <LoadingBackdrop open={!completedPurchaseRequestsLoaded} />
                )}
              </Box>
            ),
          },
        ]}
      />
    </PortalPageContent>
  );
};
